import { useState, useEffect } from "react";
import { message } from "antd";
import db from "../../../../database";
import messages from "../../../../errors/messages.en-IN.json";
import useTransactionStore from "../../../../stores/useTransactionStore";
import { getPricingRules } from "../../../../utils/pricingRulesCache";

/**
 * useDiscountOperations
 *
 * Manages:
 *  - Manual discount types (loaded from pricing rules)
 *  - External coupon types (loaded from pricing rules)
 *  - Selected discount / coupon type setters with auto-fill logic
 *  - Manual discount keypad handler
 *  - Discount additional-info form submission (approval flow)
 *  - External coupon types state
 */
const useDiscountOperations = ({
  tillData,
  manualDiscountModalVisible,
  manualDiscountInput,
  setManualDiscountInput,
  setManualDiscountTypes,
  setEnableManualDiscountInput,
  manualDiscountTypes,
  setSelectedManualDiscountTypeValue,
  manualDiscountForm,
  enableManualDiscountInput,
  discountVerificationData,
  discountAppliedValues,
  totalBillDiscountData,
  discountApproval,
  processManualDiscount,
  discountAdditionalInfoForm,
}) => {
  const [externalCouponTypes, setExternalCouponTypes] = useState([]);
  const [selectedExternalCouponType, setSelectedExternalCouponTypeValue] = useState("");

  // Load manual discount types whenever the modal opens
  useEffect(() => {
    const pr = getPricingRules();
    const bunitId = tillData.tillAccess.csBunit.csBunitId;
    const manualPricingRules = pr.filter((rule) => {
      if (rule.manualDiscount === "Y") {
        if (rule.excludeBusinessUnits === "Y") {
          if (rule.mPricingBUnits.length === 0) return rule;
          const index = rule.mPricingBUnits.findIndex((Bunit) => Bunit.mBunitPricingId !== bunitId);
          if (index !== -1) {
            return rule;
          }
        } else {
          const index = rule.mPricingBUnits.findIndex((Bunit) => Bunit.mBunitPricingId === bunitId);
          if (index >= 0) {
            return rule;
          }
        }
      }
    });
    setManualDiscountTypes([...manualPricingRules]);
  }, [manualDiscountModalVisible]);

  // Load external coupon types once on mount
  useEffect(() => {
    const pr = getPricingRules();
    const bunitId = tillData.tillAccess.csBunit.csBunitId;
    const manualPricingRules = pr.filter((rule) => {
      if (rule.iscoupon === "Y") {
        if (rule.excludeBusinessUnits === "Y") {
          if (rule.mPricingBUnits.length === 0) return rule;
          const index = rule.mPricingBUnits.findIndex((Bunit) => Bunit.mBunitPricingId !== bunitId);
          if (index !== -1) {
            return rule;
          }
        } else {
          const index = rule.mPricingBUnits.findIndex((Bunit) => Bunit.mBunitPricingId === bunitId);
          if (index >= 0) {
            return rule;
          }
        }
      }
    });
    setExternalCouponTypes([...manualPricingRules]);
  }, []);

  // Keep form field in sync with manualDiscountInput state
  useEffect(() => {
    manualDiscountForm.setFieldsValue({
      discountValue: manualDiscountInput,
    });
  }, [manualDiscountInput]);

  const setSelectedExternalCouponType = (value) => {
    if (value !== "") {
      const mdi = externalCouponTypes.findIndex((md) => md.mPricingrulesId === value);
      setSelectedExternalCouponTypeValue(externalCouponTypes[mdi]);
      const discountValue =
        externalCouponTypes[mdi]?.discountType === "V"
          ? (externalCouponTypes[mdi].amountDiscount ?? "")
          : (externalCouponTypes[mdi]?.percentageDiscount ?? "");
      if (discountValue) {
        setManualDiscountInput(discountValue);
        setEnableManualDiscountInput(true);
      } else {
        setManualDiscountInput("");
        setEnableManualDiscountInput(false);
      }
    }
  };

  const setSelectedManualDiscountType = (value) => {
    if (value !== "") {
      const mdi = manualDiscountTypes.findIndex((md) => md.mPricingrulesId === value);
      setSelectedManualDiscountTypeValue(manualDiscountTypes[mdi]);
      const discountValue =
        manualDiscountTypes[mdi]?.discountType === "V"
          ? (manualDiscountTypes[mdi].amountDiscount ?? "")
          : (manualDiscountTypes[mdi]?.percentageDiscount ?? "");
      if (discountValue) {
        setManualDiscountInput(discountValue);
        setEnableManualDiscountInput(true);
      } else {
        setManualDiscountInput("");
        setEnableManualDiscountInput(false);
      }
    }
  };

  const handleManualDiscountKeyPress = (value) => {
    if (!enableManualDiscountInput) {
      if (manualDiscountInput === "" && value === "x") {
        manualDiscountForm.setFieldsValue({ discountValue: "" });
        setManualDiscountInput("");
      } else if (value === "x") {
        const updatedValue = manualDiscountInput.toString().slice(0, -1);
        manualDiscountForm.setFieldsValue({ discountValue: updatedValue });
        setManualDiscountInput(updatedValue);
      } else if (value === "clear") {
        manualDiscountForm.setFieldsValue({ discountValue: "" });
        setManualDiscountInput("");
      } else {
        const newInput = `${manualDiscountInput}${value}`;
        manualDiscountForm.setFieldsValue({ discountValue: newInput });
        setManualDiscountInput(newInput);
      }
    }
  };

  const discountInforApplication = async (values) => {
    try {
      if (!discountVerificationData) {
        message.error("Discount data not found. Please try again.");
        return;
      }

      const formatted = {
        ...values,
        DateOfBirth: values.DateOfBirth?.format("DD-MM-YYYY"),
      };
      useTransactionStore.getState().setDiscountAdditionalInfo(formatted);

      // Approval was already validated in applyManualDiscount which found matchingApproval.
      // Do NOT re-trigger the interactive approval dialog here — it opens behind the
      // drawer and hangs forever. Just pass matchingApproval to processManualDiscount
      // which handles the parked-bill approval flow internally.
      await processManualDiscount(discountVerificationData.pricingRule, discountVerificationData.matchingApproval, discountVerificationData.values);
      discountAdditionalInfoForm.resetFields();
    } catch (err) {
      console.error("discountInforApplication error:", err);
      message.error("Failed to apply discount. Please try again.");
    }
  };

  return {
    externalCouponTypes,
    setExternalCouponTypes,
    selectedExternalCouponType,
    setSelectedExternalCouponType,
    setSelectedManualDiscountType,
    handleManualDiscountKeyPress,
    discountInforApplication,
  };
};

export default useDiscountOperations;
